import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

export type PersonSource = 'local' | 'wikipedia';
export type PersonStatus = 'ALIVE' | 'DEAD';

/** Matches ghoulpool-api's PersonSearchResult — `id` is set for source='local'
 *  (usable directly as a Pick's personId), null for 'wikipedia' until selected. */
export interface PersonSearchResult {
  source: PersonSource;
  id: number | null;
  name: string;
  knownFor: string | null;
  dateOfBirth: string | null;
  dateOfDeath: string | null;
  causeOfDeath: string | null;
  wikipediaUrl: string | null;
  summary: string | null;
  /** Wikipedia's thumbnail image URL, if known (F-005). */
  imageUrl: string | null;
  status: PersonStatus;
  /** F-033 — alternate names (stage/ring/pen names, etc.) — persisted
   *  aliases for a local hit, best-effort detected name(s) for a
   *  Wikipedia candidate. */
  aliases: string[];
}

export interface SelectPersonInput {
  name: string;
  knownFor?: string;
  dateOfBirth?: string;
  dateOfDeath?: string;
  causeOfDeath?: string;
  wikipediaUrl?: string;
  imageUrl?: string;
  /** F-033 — carries a Wikipedia candidate's detected alias(es) through
   *  to the catalog on selection. */
  aliases?: string[];
}

export interface Person {
  id: number;
  name: string;
  knownFor: string | null;
  dateOfBirth: string | null;
  dateOfDeath: string | null;
  causeOfDeath: string | null;
  wikipediaUrl: string | null;
  imageUrl: string | null;
  status: PersonStatus;
  aliases: string[];
}

/** F-009 Milestone B — site-admin correction. A field set to `null`
 *  clears it; an omitted field is left untouched (see PATCH /people/:id).
 *  F-033 — `aliases`, if present, fully replaces the alias list (not an
 *  incremental add/remove) — omit to leave existing aliases untouched. */
export interface UpdatePersonInput {
  name?: string;
  knownFor?: string | null;
  dateOfBirth?: string | null;
  dateOfDeath?: string | null;
  causeOfDeath?: string | null;
  wikipediaUrl?: string | null;
  imageUrl?: string | null;
  aliases?: string[];
}

export interface ListPeopleResult {
  items: Person[];
  total: number;
}

/** One input name's bulk-lookup result (F-027) — only returned for names
 *  with no local match (mirrors ghoulpool-api's BulkLookupResult). Empty
 *  `candidates` means Wikipedia found nothing either. */
export interface BulkLookupResult {
  name: string;
  candidates: PersonSearchResult[];
}

/** Age as of death, or current age if still alive. Client-side because it's
 *  simple deterministic math the caller already has both dates for. Shared
 *  between the picks page and the competition detail page's own-picks list. */
export function ageFor(person: {
  dateOfBirth: string | null;
  dateOfDeath: string | null;
}): number | null {
  if (!person.dateOfBirth) return null;
  const birth = new Date(person.dateOfBirth);
  const asOf = person.dateOfDeath ? new Date(person.dateOfDeath) : new Date();
  let age = asOf.getUTCFullYear() - birth.getUTCFullYear();
  const hadBirthdayThisYear =
    asOf.getUTCMonth() > birth.getUTCMonth() ||
    (asOf.getUTCMonth() === birth.getUTCMonth() &&
      asOf.getUTCDate() >= birth.getUTCDate());
  if (!hadBirthdayThisYear) age--;
  return age;
}

/** Mirrors AuthService/CompetitionsService's pattern. */
@Injectable({ providedIn: 'root' })
export class PeopleService {
  private readonly http = inject(HttpClient);

  /** `localOnly` (F-005) skips the Wikipedia fallback entirely — used by the
   *  picks page's debounced as-you-type search so partial queries never hit
   *  Wikipedia; the explicit Enter/Search action omits it.
   *
   *  `includeDead` (F-028) surfaces already-DEAD people from the local
   *  catalog — only the site-admin "recreate historical entries" flow
   *  passes this; the live picks page never does.
   *
   *  `includeWikipedia` (F-031) — a local hit otherwise blocks Wikipedia
   *  entirely, with no way to reach the actual person you're looking for
   *  if the catalog only has unrelated near-matches. The "Search Wikipedia
   *  anyway" action sets this to also check Wikipedia even when the
   *  catalog already returned results. */
  search(
    q: string,
    limit = 10,
    localOnly = false,
    includeDead = false,
    includeWikipedia = false,
  ): Promise<PersonSearchResult[]> {
    return firstValueFrom(
      this.http.get<PersonSearchResult[]>(
        `${environment.apiBaseUrl}/people/search`,
        { params: { q, limit, localOnly, includeDead, includeWikipedia } },
      ),
    );
  }

  /** F-027 — batch version of search(), for recreating a year's worth of
   *  entries at once. Returns only the names with no local match, each
   *  with its Wikipedia candidates. Site-admin only. */
  bulkLookup(names: string[]): Promise<BulkLookupResult[]> {
    return firstValueFrom(
      this.http.post<BulkLookupResult[]>(
        `${environment.apiBaseUrl}/people/bulk-lookup`,
        { names },
      ),
    );
  }

  /** Persists a Wikipedia candidate (or re-selects a local one) into the
   *  catalog — the only path that returns a real Person.id for a candidate. */
  select(input: SelectPersonInput): Promise<Person> {
    return firstValueFrom(
      this.http.post<Person>(`${environment.apiBaseUrl}/people`, input),
    );
  }

  /** On-demand live Wikipedia check — "Your Picks" redesign. `died: false`
   *  means Wikipedia still shows them alive as of this check. */
  checkForDeath(personId: number): Promise<{ died: boolean; person: Person }> {
    return firstValueFrom(
      this.http.post<{ died: boolean; person: Person }>(
        `${environment.apiBaseUrl}/people/${personId}/death-check`,
        {},
      ),
    );
  }

  /** F-009 Milestone B — site-admin browsing list, paginated. Unlike
   *  search(), includes DEAD people and doesn't hit Wikipedia. */
  list(params: { q?: string; page?: number; limit?: number } = {}): Promise<ListPeopleResult> {
    return firstValueFrom(
      this.http.get<ListPeopleResult>(`${environment.apiBaseUrl}/people`, {
        params: {
          ...(params.q ? { q: params.q } : {}),
          ...(params.page ? { page: params.page } : {}),
          ...(params.limit ? { limit: params.limit } : {}),
        },
      }),
    );
  }

  /** F-009 Milestone B — full detail for the admin Person modal. */
  getOne(id: number): Promise<Person> {
    return firstValueFrom(
      this.http.get<Person>(`${environment.apiBaseUrl}/people/${id}`),
    );
  }

  /** F-009 Milestone B — site-admin correction of an existing catalog row. */
  update(id: number, input: UpdatePersonInput): Promise<Person> {
    return firstValueFrom(
      this.http.patch<Person>(`${environment.apiBaseUrl}/people/${id}`, input),
    );
  }

  /** F-009 Milestone B — permanent site-admin delete (was F-006's dev-only
   *  cleanup tool). 409s if any Pick still references this person. */
  remove(id: number): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(`${environment.apiBaseUrl}/people/${id}`),
    );
  }
}
